import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { YourRaces, NoRacesYet } from './YourRaces';
import type { UserRacesResponse } from './home-types';

/**
 * My races — everything the user has sent us, in full.
 *
 * The homepage module shows the first few; this is the destination behind the
 * nav item and behind "See all".
 */
export function MyRacesPage() {
  const { isAuthenticated } = useAuthStore();
  const [data, setData] = useState<UserRacesResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<UserRacesResponse>('/me/races', { silent: true })
      .then((res) => live && setData(res))
      // The endpoint may not exist yet — an empty list is the honest fallback,
      // not an error the user can act on.
      .catch(() => live && setData({ items: [], total: 0 }));
    return () => {
      live = false;
    };
  }, []);

  if (!isAuthenticated) return <Navigate to="/login?redirect=%2Fmy-races" replace />;

  return (
    <div className="min-h-[70vh] bg-viewer-deep px-4 py-9 sm:px-8">
      <Helmet>
        <title>My races - RegattaReplay™</title>
      </Helmet>
      <div className="mx-auto max-w-2xl">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <h1 className="font-viewer-display text-[28px] font-bold text-viewer-ink">My races</h1>
            <p className="mt-0.5 text-[13px] text-viewer-muted">
              Races you sent us, and the ones we’ve built for you.
            </p>
          </div>
          <Link
            to="/create-race"
            className="ml-auto rounded-lg bg-brand-ink px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-brand-dark"
          >
            Create your own race
          </Link>
        </div>

        <div className="mt-7">
          {!data && !failed && <p className="text-sm text-viewer-faint">Loading…</p>}
          {data && data.items.length > 0 && <YourRaces races={data.items} showHeader={false} />}
          {data && data.items.length === 0 && <NoRacesYet />}
        </div>
      </div>
    </div>
  );
}
